import Button, { type ButtonProps } from '@mui/material/Button';
import { Link as RouterLink } from 'react-router';

/**
 * The button roles from the design system. Rank matters: one `primary` per
 * screen, everything else steps down (IA §5).
 *
 * `primaryOutline` is the brand red at the rank below the fill. It exists for
 * the one CTA that has to be recognisable as the same action in three places
 * at once — the free-advice route, which appears in the header, the phone
 * action bar and the drawer — while never outranking the quotation button it
 * sits beside. `secondary` would have done the stepping down, but it resolves
 * to the ink colour, so the same button read as brand red in one place and
 * near-black in another.
 */
export type AppButtonVariant = 'primary' | 'primaryOutline' | 'secondary' | 'ghost' | 'whatsapp';

const variantMap: Record<AppButtonVariant, Pick<ButtonProps, 'variant' | 'color'>> = {
  primary: { variant: 'contained', color: 'primary' },
  primaryOutline: { variant: 'outlined', color: 'primary' },
  secondary: { variant: 'outlined', color: 'secondary' },
  ghost: { variant: 'text', color: 'secondary' },
  whatsapp: { variant: 'contained', color: 'whatsapp' },
};

/**
 * `color="primary"` alone should be enough, but some trees on this app render
 * with the default MUI theme instead of ours, which bakes in MUI's stock blue
 * (#1976d2) rather than the brand red. Reading the CSS variables directly
 * guarantees the right colour regardless of which theme instance rendered
 * the button.
 */
const primarySx = {
  bgcolor: 'var(--mui-palette-primary-main)',
  color: 'var(--mui-palette-primary-contrastText)',
  '&:hover': { bgcolor: 'var(--mui-palette-primary-dark)' },
};

/**
 * The outlined counterpart, reading the same variables for the same reason.
 *
 * The border is the brand red at full strength rather than MUI's 50% alpha:
 * at half strength beside a filled red pill it read as a disabled version of
 * that pill instead of a quieter sibling of it. The hover is an 8% wash of
 * the same red, which is the lightest tint that registers as a response.
 *
 * Legible on the site's surfaces in both schemes (5.08:1 light, 5.85:1 dark)
 * but *not* on the near-black home hero, where it measures 3.90:1 — a caller
 * that floats this button over a dark backdrop has to supply its own ink, the
 * way the header does.
 */
const primaryOutlineSx = {
  color: 'var(--mui-palette-primary-main)',
  borderColor: 'var(--mui-palette-primary-main)',
  '&:hover': {
    borderColor: 'var(--mui-palette-primary-main)',
    bgcolor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.08)',
  },
};

/** Roles that supply their own colours; the rest take MUI's. */
const roleSx: Partial<Record<AppButtonVariant, object>> = {
  primary: primarySx,
  primaryOutline: primaryOutlineSx,
};

export type AppButtonProps = Omit<ButtonProps, 'variant' | 'color'> & {
  appVariant?: AppButtonVariant;
  /** Site path: renders a client-side link styled as a button. */
  to?: string;
  /**
   * Opening the href in a new tab. `href` already comes from `ButtonProps`,
   * which renders the anchor; these two are what an external destination also
   * needs, and MUI forwards them to the root unchanged.
   */
  target?: string;
  rel?: string;
};

export function AppButton({ appVariant = 'primary', to, sx, ...rest }: AppButtonProps) {
  const styling = variantMap[appVariant];
  // Spread rather than nest: MUI's `sx` array takes style objects, not arrays,
  // so an incoming array `sx` has to be flattened into this one.
  const role = roleSx[appVariant];
  const mergedSx = role ? [role, ...(Array.isArray(sx) ? sx : [sx ?? false])] : sx;
  if (to !== undefined) {
    const linkProps = rest as Omit<ButtonProps<typeof RouterLink>, 'to'>;
    // `intent` starts the fetch on hover or focus, so the tap has nothing
    // left to wait for. It costs nothing on a page nobody opens.
    return (
      <Button
        {...styling}
        {...linkProps}
        component={RouterLink}
        to={to}
        prefetch="intent"
        {...(mergedSx ? { sx: mergedSx } : {})}
      />
    );
  }
  return <Button {...styling} {...rest} {...(mergedSx ? { sx: mergedSx } : {})} />;
}
